/**
 * Whether an export (file or "Copy chart.json") may go ahead without asking (W2-09 publish-import; DESIGN.md §5.11
 * step 3, §4.16): it waits for a checker pass that is still running (bounded by CHECK_WAIT_MS), then reads the
 * blocking problems as they are NOW (not as last rendered). Blocking problems, or a pass that did not finish in time,
 * ask the "Export anyway" confirmation first.
 */

import type { AppServices } from '../../app/registry/types';
import type { DocStore } from '../../state/types';
import { CHECK_WAIT_MS, checkRunning, waitForCheck } from './checkerLink';
import { blockingIssues, localBlocking, songLengthSec, type PreflightIssue } from './preflight';
import { m } from './publish.messages';

/**
 * The problems that block exporting the store's current state (fatal checker problems plus the export's own checks).
 * `chartOnly`: of a chart file alone (no song, files, file names or song-select preview to check).
 */
export function currentBlocking(
  services: Pick<AppServices, 'problems' | 'audio'>,
  store: Pick<DocStore, 'doc' | 'idx'>,
  chartOnly: boolean,
): PreflightIssue[] {
  const songSec = songLengthSec(store.doc, services.audio.music);
  return blockingIssues(
    localBlocking(store.doc, store.idx, songSec, chartOnly),
    services.problems.problems(),
    chartOnly,
  );
}

/** Waits for a running checker pass; true when it finished (or none ran). See `waitForCheck`. */
export function settleCheck(
  services: Pick<AppServices, 'problems'>,
  hooks: { readonly onWait?: () => void; readonly cancelled?: () => boolean } = {},
): Promise<boolean> {
  if (!checkRunning(services.problems)) return Promise.resolve(true);
  hooks.onWait?.();
  return waitForCheck(services.problems, CHECK_WAIT_MS, hooks.cancelled);
}

/**
 * Asks before exporting what may not load in Phira: `blocking` problems ("Export with problems?"), or, with none
 * known, a checker pass that had not finished (`checkFinished` false). True when the export may go ahead.
 */
export function confirmExport(
  services: Pick<AppServices, 'ui'>,
  blocking: number,
  checkFinished: boolean,
): Promise<boolean> {
  if (blocking > 0) {
    return services.ui.confirm({
      title: { key: m.exportAnywayTitle },
      body: { key: m.exportAnywayBody, params: { count: blocking } },
      confirm: { key: m.exportAnywayConfirm },
      danger: true,
    });
  }
  if (!checkFinished) {
    return services.ui.confirm({
      title: { key: m.checkPendingTitle },
      body: { key: m.checkPendingBody },
      confirm: { key: m.exportAnywayConfirm },
      danger: true,
    });
  }
  return Promise.resolve(true);
}

/**
 * The whole clearance of an export: wait for a running pass, then ask when something blocks it (or the pass did not
 * finish). `cancelled`: the caller went away while waiting (nothing is asked then, the result is false).
 */
export async function clearForExport(
  services: Pick<AppServices, 'problems' | 'audio' | 'ui'>,
  store: Pick<DocStore, 'doc' | 'idx'>,
  chartOnly: boolean,
  hooks: { readonly onWait?: () => void; readonly onWaitEnd?: () => void; readonly cancelled?: () => boolean } = {},
): Promise<boolean> {
  const wait = { started: false };
  const finished = await settleCheck(services, {
    onWait: () => {
      wait.started = true;
      hooks.onWait?.();
    },
    ...(hooks.cancelled !== undefined ? { cancelled: hooks.cancelled } : {}),
  });
  if (wait.started) hooks.onWaitEnd?.();
  if (hooks.cancelled?.() === true) return false;
  return confirmExport(services, currentBlocking(services, store, chartOnly).length, finished);
}
